import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StudentBarcodes } from "@/components/administration/StudentBarcodes";
import { BookOpen, DollarSign, ClipboardCheck, HeadphonesIcon, MousePointerClick, Bell, BellOff, MapPin, Loader2, QrCode, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "./StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StudentProfileCard } from "@/components/student/StudentProfileCard";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { AnimatedAnnouncementFeed } from "@/components/announcements/AnimatedAnnouncementFeed";
import { useNotificationPermission } from "@/hooks/useNotificationPermission";
import { useLocationSharing } from "@/hooks/useLocationSharing";
import { useQrVisibility } from "@/hooks/useQrVisibility";


function StudentDashboardSkeleton() {
  return (
    <>
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-32 -mt-2" />
      <Skeleton className="h-32 w-full rounded-lg" />
      <div className="grid gap-2 sm:gap-3 grid-cols-1 lg:grid-cols-2">
        <Skeleton className="h-48 rounded-lg" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    </>
  );
}

export function StudentDashboard() {
  const { user } = useAuth();
  const { permission, requestPermission, sendTestNotification } = useNotificationPermission();
  const { checkVisibility } = useQrVisibility();
  const navigate = useNavigate();

  const { data: student, isLoading: studentLoading } = useQuery({
    queryKey: ["student-self", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, nactevet_reg_number, phone, program, nta_level, status, sit_status, user_id, barcode_admin, barcode_academic, barcode_examination, barcode_accounting, location_shared_at, class")
        .eq("user_id", user!.id)
        .single();
      return data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const { shareLocation, isSharing, locationGranted } = useLocationSharing(student?.location_shared_at);

  const { data: registrations = [] } = useQuery({
    queryKey: ["student-registrations", student?.id],
    queryFn: async () => {
      const { data } = await supabase.from("registrations").select("id, semester, academic_year, status, courses(course_code, course_name)").eq("student_id", student!.id).order("created_at", { ascending: false });
      return data ?? [];
    },
    enabled: !!student,
  });

  const { data: invoiceStats } = useQuery({
    queryKey: ["student-invoices", student?.id],
    queryFn: async () => {
      const { data } = await supabase.from("invoices").select("amount, status").eq("student_id", student!.id);
      const outstanding = (data ?? []).filter(i => i.status === "unpaid").reduce((s, i) => s + Number(i.amount), 0);
      const paid = (data ?? []).filter(i => i.status === "paid").reduce((s, i) => s + Number(i.amount), 0);
      return { outstanding, paid, total: data?.length ?? 0 };
    },
    enabled: !!student,
  });

  const { data: requests = [] } = useQuery({
    queryKey: ["student-requests", student?.id],
    queryFn: async () => {
      const { data } = await supabase.from("service_requests").select("id, subject, status, created_at").eq("student_id", student!.id).order("created_at", { ascending: false }).limit(5);
      return data ?? [];
    },
    enabled: !!student,
  });

  const { data: announcements = [] } = useQuery({
    queryKey: ["student-announcements"],
    queryFn: async () => {
      const { data } = await supabase
        .from("announcements")
        .select("id, title, content, priority, category, created_at, file_url")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  const { data: activeSessions = [] } = useQuery({
    queryKey: ["active-attendance-sessions"],
    queryFn: async () => {
      const { data } = await supabase
        .from("attendance_sessions")
        .select("id, status")
        .eq("status", "open")
        .limit(1);
      return data ?? [];
    },
    staleTime: 30 * 1000,
  });

  const hasActiveSession = activeSessions.length > 0;

  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n}`;

  if (studentLoading) {
    return <StudentDashboardSkeleton />;
  }

  if (!student) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Welcome</CardTitle>
          <CardDescription>No student record found linked to your account. Please contact the administration.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const isRegistered = registrations.length > 0;
  const latestReg = registrations[0] as any;

  return (
    <>
      <h1 className="text-lg sm:text-xl md:text-2xl font-bold truncate">
        Welcome, {student.first_name} <span className="inline-block animate-wave origin-[70%_70%]">👋</span>
      </h1>
      <p className="text-muted-foreground text-xs sm:text-sm -mt-2">Student Dashboard</p>

      {/* 1. Student Profile Card */}
      <StudentProfileCard student={student as any} />

      {/* 2. My Registration */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            My Registration
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!isRegistered ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                You have not been registered. Go to the registrar office for registration.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="text-sm font-medium text-primary">
                  You are registered for {student.program || "your program"}, {latestReg?.semester || "current semester"}, {latestReg?.academic_year || "current academic year"}.
                </p>
              </div>
              <div className="table-responsive">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Course</TableHead>
                      <TableHead>Semester</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrations.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.courses?.course_code} - {r.courses?.course_name}</TableCell>
                        <TableCell>{r.semester}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 3. St. David Noticeboard */}
      <AnimatedAnnouncementFeed
        announcements={announcements}
        title="St. David Noticeboard"
        emptyMessage="No announcements at this time"
      />

      {/* 4. Examination Results CTA */}
      <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/examination")}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />Examination Results
          </CardTitle>
          <CardDescription>View your published exam results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-4 text-center">
<Button 
              className="animate-pulse gap-2 text-base px-6 py-3 h-auto bg-accent hover:bg-accent/90 text-accent-foreground" 
              onClick={(e) => { e.stopPropagation(); navigate("/examination"); }}
            >
              <MousePointerClick className="h-5 w-5" />
              Click Here to View Results
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 5. Fee Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />Fee Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoiceStats?.total === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No fee records found</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-xs text-muted-foreground">Paid</p>
                <p className="text-lg font-bold text-primary">{fmt(invoiceStats?.paid ?? 0)}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-xs text-muted-foreground">Outstanding</p>
                <p className="text-lg font-bold text-destructive">{fmt(invoiceStats?.outstanding ?? 0)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 6. Notifications & Location */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {permission === "granted" ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {permission === "unsupported" ? (
              <p className="text-xs text-muted-foreground">Your browser does not support notifications</p>
            ) : permission === "granted" ? (
              <div className="space-y-2">
                <Badge variant="secondary" className="bg-primary/10 text-primary">Enabled</Badge>
                <p className="text-xs text-muted-foreground">You'll receive important updates and alerts.</p>
                <Button type="button" variant="outline" size="sm" className="text-xs" onClick={sendTestNotification}>
                  Send Test
                </Button>
              </div>
            ) : permission === "denied" ? (
              <div className="space-y-2">
                <Badge variant="destructive">Blocked</Badge>
                <p className="text-xs text-muted-foreground">
                  Notifications are blocked. Please enable them in your browser settings to receive important updates.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Enable notifications to receive important updates about your studies.</p>
                <Button
                  type="button"
                  size="sm"
                  className="text-xs gap-1"
                  onClick={async () => {
                    const result = await requestPermission();
                    if (result === "granted") sendTestNotification();
                  }}
                >
                  <Bell className="h-3.5 w-3.5" />
                  Enable Notifications
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {!locationGranted && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Allow this app to access your device location for attendance and security purposes.</p>
                <Button
                  type="button"
                  size="sm"
                  className="text-xs gap-1"
                  onClick={shareLocation}
                  disabled={isSharing}
                >
                  {isSharing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />}
                  {isSharing ? "Allowing..." : "Allow My Location"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <HeadphonesIcon className="h-5 w-5 text-primary" />My Support
          </CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No support requests</p>
          ) : (
            <div className="table-responsive">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.subject}</TableCell>
                      <TableCell><StatusBadge status={r.status} /></TableCell>
                      <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* My QR Code Card */}
      {student && (student.barcode_admin || student.barcode_academic || student.barcode_examination || student.barcode_accounting) && (() => {
        // All QR codes follow superadmin-configured visibility rules
        const studentClass = student.class || null;
        const visAdmin = checkVisibility("admin", true, studentClass);
        const visAcademic = checkVisibility("academic", true, studentClass);
        const visExam = checkVisibility("examination", true, studentClass);
        const visAccounting = checkVisibility("accounting", true, studentClass);

        // Students always see their own identity QR codes (gated only by master override).
        // Time/geo/role/class rules apply only to staff-side scanning.
        const allItems = [
          { label: "Administration", vis: visAdmin, show: visAdmin.isVisible },
          { label: "Academic", vis: visAcademic, show: visAcademic.isVisible },
          { label: "Examination", vis: visExam, show: visExam.isVisible },
          { label: "Accounting", vis: visAccounting, show: visAccounting.isVisible },
        ];
        const anyHidden = allItems.some(i => !i.show);

        return (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <QrCode className="h-5 w-5 text-primary" />My QR Code Card
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {anyHidden && (
                <div className="space-y-1">
                  {allItems.filter(i => !i.show).map(item => (
                    <div key={item.label} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <EyeOff className="h-3 w-3 shrink-0" />
                      <span>{item.label}: {item.vis.reason}</span>
                    </div>
                  ))}
                </div>
              )}
              <StudentBarcodes
                studentName={`${student.first_name} ${student.last_name}`}
                barcodeAdmin={visAdmin.isVisible ? student.barcode_admin : null}
                barcodeAcademic={visAcademic.isVisible ? student.barcode_academic : null}
                barcodeExamination={visExam.isVisible ? student.barcode_examination : null}
                barcodeAccounting={visAccounting.isVisible ? student.barcode_accounting : null}
                rotating
              />
            </CardContent>
          </Card>
        );
      })()}
    </>
  );
}
